import { useState, useEffect } from 'react';

export function useIsMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function safeDate(d: Date | string | number | null | undefined): Date {
  if (d == null) return new Date(0);
  const date = d instanceof Date ? d : new Date(d);
  return isNaN(date.getTime()) ? new Date(0) : date;
}

/**
 * Split a masthead title into a display-caps HEAD (first word) and an
 * italic-serif TAIL (everything after the first space). Single-word
 * titles get an empty tail. Feeds <PageTitle> / <ZineHeader>.
 *
 * Previously re-implemented inline in /settings, /profile and /active
 * under three different names — unified in the 1394–1411 review sweep.
 */
export function splitZineTitle(raw: string): { head: string; tail: string } {
  const i = raw.indexOf(' ');
  if (i < 0) return { head: raw, tail: '' };
  return { head: raw.slice(0, i), tail: raw.slice(i + 1) };
}

export function formatDate(
  d: Date | string | number | null | undefined,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
  fallback = '—'
): string {
  if (d == null) return fallback;
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString(locale, options);
}

/**
 * Inclusive count of calendar days a project spanned, from its start
 * (startDate ?? createdAt) to its finish (finishDate ?? finishedAt ??
 * updatedAt). Calendar-day based (time-of-day stripped via local midnights)
 * so a project started and finished on the same day counts as 1, not 0, and
 * a partial last day isn't lost. Returns null when either endpoint is
 * missing/invalid or the finish precedes the start.
 * Added 2026-06-08 (Veronika): surfaces "how long it took" on finished cards.
 */
/**
 * The auto-frozen `startedAt` a project should carry after a save that may
 * change its status (Veronika 2026-06-12): a 'future' project is just a plan
 * with no start date; the moment it leaves 'future' IS its start. Pure so the
 * transition table is unit-testable (projectDuration.test.ts).
 *
 * - create (prevStatus null) as anything but 'future' → stamp `now`
 * - any → 'future' → undefined (back to a plan; clears the stamp)
 * - 'future' → anything else → stamp `now`
 * - every other edit → keep the existing stamp (legacy rows that left
 *   'future' before this field existed stay undefined → createdAt fallback;
 *   stamping them at edit time would invent a start they didn't have)
 */
export function nextStartedAt(
  prevStatus: 'in-progress' | 'hibernating' | 'future' | 'finished' | null,
  prevStartedAt: Date | undefined,
  status: 'in-progress' | 'hibernating' | 'future' | 'finished',
  now: Date
): Date | undefined {
  if (status === 'future') return undefined;
  if (prevStatus === null || prevStatus === 'future') return now;
  return prevStartedAt;
}

export function projectSpanDays(
  start: Date | string | number | null | undefined,
  finish: Date | string | number | null | undefined
): number | null {
  if (start == null || finish == null) return null;
  const s = safeDate(start);
  const f = safeDate(finish);
  if (s.getTime() === 0 || f.getTime() === 0) return null;
  const sMid = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
  const fMid = new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime();
  if (fMid < sMid) return null;
  return Math.round((fMid - sMid) / 86_400_000) + 1;
}
